import { useEffect, useState } from 'react';
import { CustomInvestorLayout, Footer } from '../commonComponents';
import {
  AppBar,
  Box,
  Button,
  CardMedia,
  Paper,
  Stack,
  Typography,
  Theme,
  CircularProgress,
} from '@mui/material';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { getDigilockerData, postCKYCDataValidation } from '../../redux-store/actions/onBoarding';
import { CKYCDataValidationRes, GetDigilockerDataResponseBody } from '../../redux-store/types/api-types';
import { getDigiLockerErrorMessage } from '../../utils/constant';
import { SuccessIcon } from '../../assets/SvgIcons';
import { VERIFY_LOGIN_OTP_SUCCESS } from '../../redux-store/types/auth';
import { showError } from '../../redux-store/actions/auth';
import { RootStateType } from '../../redux-store/reducers';

export function Digilocker(): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { applicationId = "" } = useSelector((state: RootStateType) => state.id);

  const code = queryParams.get('code');
  const error = queryParams.get('error');
  const state = queryParams.get('state');

  const { params } = useRouteMatch();

  const status = queryParams.get('status');
  const digio_doc_id = queryParams.get('digio_doc_id');
  const { referenceId } = params as { referenceId: string };

  const success = status === 'success';

  const [response, setResponse] = useState<Partial<GetDigilockerDataResponseBody>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [errorResult, setErrorResult] = useState<string>('');
  const [errorLoading, setErrorLoading] = useState<boolean>(false);

  useEffect(() => {
    if ((!referenceId && !success && !digio_doc_id) && (!code && !error && !state)) {
      history.replace('/kyc-doc-selection');
      return;
    }

    if ((referenceId && digio_doc_id && success) || (code && state && !error)) {
      setLoading(true);
      (async () => {
        try {
          const res = await (dispatch(
            getDigilockerData({ state: referenceId ?? state, code: digio_doc_id ?? code ?? '' })
          )) as unknown as Partial<GetDigilockerDataResponseBody>;

          if (!res) {
            dispatch(showError('Something went wrong, please try again'));
            // history.replace('/kyc-doc-selection');
          } else {
            setResponse(res);
            if (res?.token?.includes('Bearer')) {
              dispatch({
                type: VERIFY_LOGIN_OTP_SUCCESS,
                body: {
                  token: res?.token?.split('Bearer ')[1]
                }
              })
            } else {
              dispatch({
                type: VERIFY_LOGIN_OTP_SUCCESS,
                body: {
                  token: res?.token
                }
              })
            }
          }
        } catch (e) {
          dispatch(showError('An unexpected error occurred'));
          // history.replace('/kyc-doc-selection');
        } finally {
          setLoading(false);
        }
      })();
    }
    if ((referenceId && !success) || error) {
      (
        async () => {
          try {
            setErrorLoading(true);
            const res = await dispatch(postCKYCDataValidation({ applicationId: `${applicationId}`, sentToRM: true })) as unknown as CKYCDataValidationRes;
            if (!res) {
              dispatch(showError('Something went wrong, please try again'));
              return;
            }
            setErrorResult(res.message);
          } catch (e) {
            dispatch(showError('An unexpected error occurred'));
          } finally {
            setErrorLoading(false);
          }

        }
      )()

    }
  }, [code, state, error, referenceId, success, digio_doc_id, dispatch, history]);

  if ((referenceId && !success) || error) {
    const errorMessage = getDigiLockerErrorMessage(error ?? '');

    return (
      <CustomInvestorLayout>
        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            padding: '15px',
          }}
        >
          <Box
            sx={{
              bgcolor: 'grey.50',
              maxWidth: '657px',
              minWidth: '400px',
              width: 'auto',
              padding: '20px',
              mb: 2,
              borderRadius: '24px',
              boxShadow: '0px 4px 10px 0px #0000000D',
            }}
          >
            {errorLoading ?
              (
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    maxWidth: 600,
                    width: '100%',
                    borderRadius: '16px',
                    textAlign: 'center',
                    backgroundColor: 'background.default',
                  }}
                >
                  <CircularProgress />
                  <Typography
                    variant="h2"
                    sx={{ color: 'primary.main', mb: '32px', textAlign: 'center' }}
                  >
                    We are fetching the data, please wait!
                  </Typography>
                </Paper>
              ) :
              (<Box
                sx={{
                  padding: '26px',
                  border: '1px solid #6C737F26',
                  borderRadius: '15px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  component="h1"
                  variant="h1"
                  sx={{ color: 'primary.main', mb: '32px', textAlign: 'left' }}
                >
                  Digilocker
                </Typography>
                <Typography
                  variant="body_500"
                  sx={{ color: 'primary.main', mb: '32px', textAlign: 'left' }}
                >
                  {error ? errorMessage : 'Digilocker verification has failed.'}
                </Typography>
                <Typography
                  variant="body_500"
                  sx={{ color: 'primary.main', mb: '32px', textAlign: 'left' }}
                >
                  {errorResult ?? 'Application Successfully Send to RM'}. Please reach out to your RM for further assistance.
                </Typography>
                <Typography
                  variant="body_500"
                  sx={(theme: Theme) => ({ color: theme.palette.text.primary, mb: '32px', textAlign: 'left' })}
                >
                  You can close this tab now
                </Typography>
                {/* <Button
                variant="contained"
                onClick={() => history.push('/kyc-doc-selection')}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  width: '100%',
                  height: '48px',
                  borderRadius: '12px',
                  textTransform: 'none',
                }}
              >
                Try Again
              </Button> */}
              </Box>)
            }
          </Box>
        </Box>
      </CustomInvestorLayout>

    );
  }

  const isSuccess = response.response?.toLowerCase() === 'success';

  return (
    <CustomInvestorLayout>
      <Box
        component="main"
        sx={{ flex: 1, display: 'grid', placeItems: 'center', padding: '15px' }}
      >
        {loading ? (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
              borderRadius: '16px',
              textAlign: 'center',
              backgroundColor: 'background.default',
            }}
          >
            <CircularProgress />
            <Typography
              variant="h2"
              sx={{ color: 'primary.main', mb: '32px', textAlign: 'center' }}
            >
              We are fetching the data, please wait!
            </Typography>
          </Paper>
        ) : isSuccess ? (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
              borderRadius: '16px',
              textAlign: 'center',
              backgroundColor: 'background.default',
            }}
          >
            <Typography variant="body_500" sx={{ mt: 1, mb: 1, color: 'primary.light', fontWeight: 600 }}>
              Congratulations!
            </Typography>
            <SuccessIcon />
            <Typography
              variant="h2"
              sx={(theme: Theme) => ({
                lineHeight: '25px',
                letterSpacing: '0.01px',
                color: theme.palette.text.primary,
                mb: 2,
              })}
            >
              KYC Data Fetched Successfully
            </Typography>
            <Typography
              variant="subtitle_400"
              sx={(theme: Theme) => ({
                lineHeight: '22px',
                letterSpacing: '0.02px',
                color: theme.palette.text.secondary,
                textAlign: 'center',
                px: 2,
                mb: 2,
              })}
            >
              Your details have been successfully fetched from DigiLocker.
            </Typography>
            <Typography
              variant="subtitle_400"
              sx={(theme: Theme) => ({
                lineHeight: '22px',
                letterSpacing: '0.02px',
                color: theme.palette.text.secondary,
                textAlign: 'center',
                px: 2,
                mb: 2,
              })}
            >
              You can now proceed to complete your onboarding.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 4, px: 4, py: 1 }}
              onClick={() => {
                dispatch({ type: VERIFY_LOGIN_OTP_SUCCESS, body: { diyInvestorOnboardingEdit: true, diyIsKycDone: true } });
                dispatch(showError('Please complete your onboarding journey'));
                history.replace('/investor-onboarding-application/personal-details', {
                  id: response?.applicationId,
                });
              }}
            >
              Continue to Onboarding
            </Button>
          </Paper>
        ) : (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
              borderRadius: '16px',
              textAlign: 'center',
              backgroundColor: 'background.default',
            }}
          >
            <Typography variant="body_500" sx={{ mt: 1, mb: 1, color: 'primary.light', fontWeight: 600 }}>
              Oops!
            </Typography>
            <Typography
              variant="h2"
              sx={(theme: Theme) => ({
                lineHeight: '25px',
                letterSpacing: '0.01px',
                color: theme.palette.text.primary,
                mb: 2,
              })}
            >
              KYC Verification Failed
            </Typography>
            <Typography
              variant="subtitle_400"
              sx={(theme: Theme) => ({
                lineHeight: '22px',
                letterSpacing: '0.02px',
                color: theme.palette.text.secondary,
                textAlign: 'center',
                px: 2,
                mb: 2,
              })}
            >
              {response?.error || 'We were unable to fetch your KYC data. Please try again.'}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 4, px: 4, py: 1 }}
              onClick={() => history.push('/kyc-doc-selection', { id: response?.applicationId })}
            >
              Try Again
            </Button>
          </Paper>
        )}
        <Typography variant="body_400" color={'text.secondary'} justifySelf={'center'}>Note:Do not press back button or navigate back</Typography>
      </Box>
    </CustomInvestorLayout>
  );
}
